import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Eye, Landmark, Trash2 } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';
import { FoldCard } from './FoldCard';

/**
 * BANK INFORMATION DEL HOMEOWNER (pedido de Manuel 2026-08-10).
 *
 * Para depositarle al homeowner hay que tener su banco, routing y cuenta — y esos números
 * salen de la foto del cheque (voided check) que el cliente manda. Hasta ahora eso vivía en
 * un update suelto o en un email; acá queda EN Payments: drag & drop de la foto (se guarda
 * en Files como 'ho_bank_check') + los tres campos del banco en el claim.
 */

type BankFields = { ho_bank_name?: string | null; ho_bank_routing?: string | null; ho_bank_account?: string | null };
type BankFile = { id: string; file_name: string | null; doc_type: string; created_at: string; uploaded_by?: string | null };

const CAMPOS = [
  ['ho_bank_name', 'hob.bank'],
  ['ho_bank_routing', 'hob.routing'],
  ['ho_bank_account', 'hob.account'],
] as const;

export function HomeownerBankBox({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [f, setF] = useState<Record<string, string>>({});
  const [fotos, setFotos] = useState<BankFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const loadClaim = () => api.get<BankFields>(`/api/claims/${claimId}`).then((c) => setF({
    ho_bank_name: c.ho_bank_name ?? '',
    ho_bank_routing: c.ho_bank_routing ?? '',
    ho_bank_account: c.ho_bank_account ?? '',
  })).catch(() => {});
  const loadFotos = () => api.get<{ files: BankFile[] }>(`/api/claims/${claimId}/files`)
    .then((r) => setFotos(r.files.filter((x) => x.doc_type === 'ho_bank_check')))
    .catch(() => {});
  useEffect(() => { loadClaim(); loadFotos(); /* eslint-disable-next-line */ }, [claimId]);

  async function guardar() {
    setBusy(true); setOk(false);
    try {
      await api.patch(`/api/claims/${claimId}/info`, f);
      setOk(true);
      setTimeout(() => setOk(false), 2500);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  // Foto del cheque: sube a R2, la registra como file del claim y la etiqueta 'ho_bank_check'
  // para que en Files se distinga de cualquier otro cheque escaneado.
  async function subirFoto(file: File) {
    setDropBusy(true);
    try {
      const key = await uploadFile(file, 'misc');
      const r = await api.post<{ id: string }>(`/api/claims/${claimId}/files`, {
        fileKey: key, fileName: file.name, mimeType: file.type || null,
      });
      await api.patch(`/api/files/${r.id}`, { docType: 'ho_bank_check' });
      await loadFotos();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setDropBusy(false); }
  }

  async function verFoto(id: string) {
    try { const r = await api.get<{ url: string }>(`/api/files/${id}/url`); window.open(r.url, '_blank', 'noopener'); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  async function borrarFoto(id: string) {
    if (!window.confirm(t('hob.deleteConfirm'))) return;
    try { await api.del(`/api/files/${id}`); await loadFotos(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  return (
    /* Plegada: son tres campos que se llenan UNA vez, cuando llega la foto del cheque. El hint
       muestra el banco ya guardado, así que cerrada igual se sabe si el dato está. */
    <FoldCard
      id="pay-hobank"
      title={<><Landmark size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hob.title')}</>}
      defaultOpen={false}
      hint={ok ? t('common.saved') : (f.ho_bank_name || undefined)}
    >
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 10 }}>{t('hob.help')}</div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {CAMPOS.map(([campo, key]) => (
          <label key={campo} style={{ display: 'grid', gap: 4, fontSize: 'var(--fs-2xs)' }}>
            <span className="muted">{t(key)}</span>
            <input className="input sm" style={{ width: campo === 'ho_bank_name' ? 180 : 150, fontFamily: campo === 'ho_bank_name' ? undefined : 'var(--font-mono)' }}
              value={f[campo] ?? ''} onChange={(e) => setF((p) => ({ ...p, [campo]: e.target.value }))} />
          </label>
        ))}
        <button className="btn sm" disabled={busy} onClick={guardar}>{t('common.save')}</button>
      </div>

      {/* Foto(s) del cheque — de acá salen el routing y la cuenta. */}
      <div style={{ marginTop: 12 }}>
        <FileDrop onFile={subirFoto} accept="application/pdf,image/*" label={t('hob.drop')} busy={dropBusy} busyLabel={t('cd.uploading')} compact />
        {fotos.length > 0 && (
          <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
            {fotos.map((x) => (
              <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-xs)', borderBottom: '1px solid var(--line)', paddingBottom: 4 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{x.file_name || `file-${x.id}`}</span>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>{new Date(x.created_at).toLocaleDateString(localeUI())}</span>
                <button className="btn ghost sm" style={{ padding: '0 5px' }} onClick={() => verFoto(x.id)} title={t('hob.view')}><Eye size={13} strokeWidth={1.75} /></button>
                <button className="btn ghost sm" style={{ padding: '0 5px', color: 'var(--red)' }} onClick={() => borrarFoto(x.id)} title={t('common.delete')}><Trash2 size={13} strokeWidth={1.75} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </FoldCard>
  );
}
